import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, XIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
const privacyContent = `
**Política de Privacidad / Privacy Policy**

Última actualización / Last updated: April 2026

**Información que recopilamos / Information We Collect**
Cuando compras "Bilingual Emotion Monsters", recopilamos únicamente la información necesaria para procesar tu pedido: nombre, dirección de correo electrónico y dirección de envío (si aplica).

When you purchase "Bilingual Emotion Monsters," we only collect the information necessary to process your order: name, email address, and shipping address (if applicable).

**Uso de la información / Use of Information**
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu pedido
• Enviar confirmación de compra
• Comunicar actualizaciones importantes sobre el producto

Your personal information is used exclusively to:
• Process and deliver your order
• Send purchase confirmation
• Communicate important product updates

**Protección de datos / Data Protection**
No vendemos, intercambiamos ni transferimos tu información personal a terceros. Implementamos medidas de seguridad para proteger tu información.

We do not sell, trade, or transfer your personal information to third parties. We implement security measures to protect your information.

**Cookies**
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies.

Our site may use cookies to enhance user experience. You can configure your browser to reject cookies.

**Derechos del usuario / User Rights**
Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento contactándonos.

You have the right to access, correct, or delete your personal information at any time by contacting us.

**Cambios a esta política / Changes to This Policy**
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.

We reserve the right to update this policy at any time. Changes will be posted on this page.
`;
const termsContent = `
**Términos y Condiciones / Terms & Conditions**

Última actualización / Last updated: April 2026

**Aceptación de los términos / Acceptance of Terms**
Al acceder y utilizar este sitio web y adquirir "Bilingual Emotion Monsters", aceptas estos términos y condiciones en su totalidad.

By accessing and using this website and purchasing "Bilingual Emotion Monsters," you accept these terms and conditions in full.

**Uso del producto / Product Use**
Este material tiene fines exclusivamente informativos y educativos. El contenido no pretende sustituir el consejo de un profesional de la salud mental, psicólogo infantil o pediatra.

This material is for informational and educational purposes only. The content is not intended to replace the advice of a mental health professional, child psychologist, or pediatrician.

**Propiedad intelectual / Intellectual Property**
Todo el contenido de "Bilingual Emotion Monsters", incluyendo textos, ilustraciones, diseños y metodología, está protegido por derechos de autor. Prohibida su reproducción total o parcial sin autorización.

All content in "Bilingual Emotion Monsters," including text, illustrations, designs, and methodology, is protected by copyright. Total or partial reproduction without authorization is prohibited.

**Limitación de responsabilidad / Limitation of Liability**
El autor y el editor no asumen responsabilidad por el uso que se le dé a esta información. Los resultados pueden variar según el desarrollo individual de cada niño.

The author and publisher assume no responsibility for the use of this information. Results may vary depending on each child's individual development.

**Política de reembolso / Refund Policy**
Si no estás satisfecho con tu compra, puedes solicitar un reembolso dentro de los 30 días posteriores a la compra.

If you are not satisfied with your purchase, you may request a refund within 30 days of purchase.

**Ley aplicable / Governing Law**
Estos términos se rigen por las leyes de los Estados Unidos de América.

These terms are governed by the laws of the United States of America.
`;
export const FinalCTA = () => {
  const [modalContent, setModalContent] = useState<{
    title: string;
    body: string;
  } | null>(null);
  const openModal = (type: 'privacy' | 'terms') => {
    if (type === 'privacy') {
      setModalContent({
        title: 'Política de Privacidad / Privacy Policy',
        body: privacyContent
      });
    } else {
      setModalContent({
        title: 'Términos y Condiciones / Terms & Conditions',
        body: termsContent
      });
    }
  };
  const renderMarkdownText = (text: string) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} data-id="element-59" />;
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return <h3 key={i} className="font-display font-bold text-lg text-dark mt-6 mb-2" data-id="element-60">
            {trimmed.replace(/\*\*/g, '')}
          </h3>;
      }
      if (trimmed.startsWith('•')) {
        return <li key={i} className="ml-4 font-body text-dark/80" data-id="element-61">
            {trimmed.replace('• ', '')}
          </li>;
      }
      return <p key={i} className="font-body text-dark/80 leading-relaxed" data-id="element-62">
          {trimmed}
        </p>;
    });
  };
  return <>
      <footer id="pricing" className="w-full bg-dark text-white pt-24 pb-10 px-6 relative overflow-hidden" data-id="element-63">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-anger via-happiness to-calm" data-id="element-64"></div>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-anger/10 rounded-full blur-3xl" data-id="element-65"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-calm/10 rounded-full blur-3xl" data-id="element-66"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-67">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-68">
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-6" data-id="element-69">
              Tu Siguiente Paso
            </h2>

            <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-70">
              Has dado un paso gigante. No solo compraste un libro, sino que
              decidiste cambiar la narrativa de tu hogar. La próxima vez que
              sientas que la tormenta viene, respira, busca los crayones y llama
              a los monstruos.
            </p>

            <div className="flex items-center justify-center space-x-3 mb-12" data-id="element-71">
              <Heart className="w-6 h-6 text-anger fill-anger" data-id="element-72" />
              <p className="font-display text-2xl font-bold text-happiness" data-id="element-73">
                La conexión es mejor que la perfección.
              </p>
              <Heart className="w-6 h-6 text-anger fill-anger" data-id="element-74" />
            </div>

            <div className="mb-6" data-id="element-75">
              <span className="font-body text-white/50 line-through text-xl mr-3" data-id="element-76">
                $12.99
              </span>
              <span className="font-display text-5xl md:text-6xl font-bold text-happiness" data-id="element-77">
                $6.39
              </span>
            </div>
            <p className="font-body text-white/60 text-sm mb-8" data-id="element-78">
              Precio especial por tiempo limitado / Limited time offer
            </p>

            <motion.a href="https://vivetransforma.com/cart/51276984516903:1" target="_blank" rel="noopener noreferrer" onClick={() => trackAddToCart()} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-block px-10 py-5 bg-anger text-white font-display font-bold text-2xl rounded-2xl shadow-glow-anger hover:bg-red-500 transition-colors mb-4 no-underline" data-id="element-79">
              Obtén tu Copia Hoy — $6.39
            </motion.a>
            <p className="font-body text-white/50 text-sm mb-20" data-id="element-80">
              Acceso inmediato · Pago único y seguro
            </p>
          </motion.div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between text-sm font-body text-white/50" data-id="element-81">
            <p data-id="element-82">
              © {new Date().getFullYear()} Bilingual Emotion Monsters. All
              rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0" data-id="element-83">
              <button onClick={() => openModal('privacy')} className="hover:text-white transition-colors underline underline-offset-2 cursor-pointer" data-id="element-84">
                Privacy Policy
              </button>
              <button onClick={() => openModal('terms')} className="hover:text-white transition-colors underline underline-offset-2 cursor-pointer" data-id="element-85">
                Terms & Conditions
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-86">
        {modalContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModalContent(null)} data-id="element-87">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-88">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-89">
                <h2 className="font-display text-xl font-bold text-dark" data-id="element-90">
                  {modalContent.title}
                </h2>
                <button onClick={() => setModalContent(null)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer" data-id="element-91">
                  <XIcon className="w-5 h-5 text-dark" data-id="element-92" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto" data-id="element-93">
                {renderMarkdownText(modalContent.body)}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};